import { useEffect, useState } from 'react';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import './home.scss';
import Loading from '../../components/Loading/Loading';
import { Link } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

const Home = () => {
  const { currentUser } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Simulate loading time for smooth transition
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    // Update current time every minute
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => {
      clearTimeout(timer);
      clearInterval(timeInterval);
    };
  }, []);

  if (isLoading) {
    return <Loading inline={true} message="Loading dashboard..." />;
  }

  const quickActions = [
    {
      title: 'Add New Shareholder',
      description: 'Register a new shareholder in the system',
      icon: <PersonAddIcon />,
      link: '/users/new',
      color: 'primary',
    },
    {
      title: 'View All Shareholders',
      description: 'Browse and manage existing shareholders',
      icon: <PeopleIcon />,
      link: '/users',
      color: 'secondary',
    },
    {
      title: 'Transaction History',
      description: 'View all transactions and activities',
      icon: <AssessmentIcon />,
      link: '/all-transactions',
      color: 'tertiary',
    },
    {
      title: 'Dividend Management',
      description: 'Manage dividend distributions',
      icon: <AccountBalanceWalletIcon />,
      link: '/dividend',
      color: 'quaternary',
    },
  ];

  const systemStats = [
    {
      title: 'System Status',
      value: 'Online',
      description: 'All systems operational',
      icon: <SecurityIcon />,
      status: 'success',
    },
    {
      title: 'Data Integrity',
      value: '100%',
      description: 'All data secure and backed up',
      icon: <TrendingUpIcon />,
      status: 'success',
    },
    {
      title: 'User Role',
      value: currentUser?.staffData?.role || 'Staff',
      description: 'Your current access level',
      icon: <BusinessIcon />,
      status: 'info',
    },
  ];

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="home">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />

        {/* Welcome Header */}
        <div className="welcomeSection">
          <div className="welcomeContent">
            <div className="welcomeText">
              <h1>Welcome back, {currentUser?.staffData?.name || 'User'}!</h1>
              <p>Here's your dashboard overview for today</p>
            </div>
            <div className="welcomeTime">
              <div className="currentTime">
                <CalendarTodayIcon className="timeIcon" />
                <div className="timeInfo">
                  <div className="time">{formatTime(currentTime)}</div>
                  <div className="date">{formatDate(currentTime)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quickActionsSection">
          <div className="sectionHeader">
            <h2>Quick Actions</h2>
            <p>Frequently used functions for efficient workflow</p>
          </div>
          <div className="quickActionsGrid">
            {quickActions.map((action, index) => (
              <Link
                to={action.link}
                key={index}
                className={`actionCard ${action.color}`}
              >
                <div className="actionIcon">{action.icon}</div>
                <div className="actionContent">
                  <h3>{action.title}</h3>
                  <p>{action.description}</p>
                </div>
                <div className="actionArrow">
                  <TrendingUpIcon />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* System Overview */}
        <div className="systemOverviewSection">
          <div className="sectionHeader">
            <h2>System Overview</h2>
            <p>Current system status and information</p>
          </div>
          <div className="systemStatsGrid">
            {systemStats.map((stat, index) => (
              <div key={index} className={`statCard ${stat.status}`}>
                <div className="statIcon">{stat.icon}</div>
                <div className="statContent">
                  <div className="statValue">{stat.value}</div>
                  <div className="statTitle">{stat.title}</div>
                  <div className="statDescription">{stat.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bank Information */}
        <div className="bankInfoSection">
          <div className="bankInfoCard">
            <div className="bankIcon">
              <AccountBalanceIcon />
            </div>
            <div className="bankContent">
              <h3>Kwahu Praso Rural Bank</h3>
              <p>Shareholder's Management System</p>
              <div className="bankDetails">
                <span>Secure • Reliable • Professional</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="notificationsSection">
          <div className="notificationCard">
            <div className="notificationIcon">
              <NotificationsIcon />
            </div>
            <div className="notificationContent">
              <h4>System Notifications</h4>
              <p>All systems are running smoothly. No alerts at this time.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
